/**
 * Redeeming an invite — the one screen a child ever sees of the lock.
 *
 * The code arrives in the URL *fragment* (`/uitnodiging#bbi_…`). Fragments never
 * leave the browser, so the code is not in nginx's log, not in a Referer header,
 * and — the reason it matters most — not fetchable by the link previewer that
 * WhatsApp runs over every URL, which would otherwise burn a single-use invite
 * before the child taps it.
 *
 * For the same reason redemption is a POST from this page rather than a GET on
 * the link itself.
 */

import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, ApiError } from '@/lib/api';
import { useAuth } from '@/store/auth';

type Phase = 'redeeming' | 'done' | 'failed' | 'nocode';

export default function Uitnodiging() {
  const navigate = useNavigate();
  const refresh = useAuth((state) => state.refresh);
  const [phase, setPhase] = useState<Phase>('redeeming');
  const [message, setMessage] = useState<string>('');
  const started = useRef(false);

  useEffect(() => {
    // Once. React 18 mounts effects twice in development, and a second attempt
    // would redeem nothing and report the first one's code as already used.
    if (started.current) return;
    started.current = true;

    const code = window.location.hash.replace(/^#/, '').trim();
    if (!code) {
      setPhase('nocode');
      return;
    }

    // Clear it from the address bar straight away, so it does not end up in
    // history, in a screenshot, or in the next person's autocomplete.
    window.history.replaceState(null, '', '/uitnodiging');

    void (async () => {
      try {
        await api('/api/auth/redeem', { method: 'POST', body: JSON.stringify({ code }) });
        await refresh();
        setPhase('done');
        window.setTimeout(() => navigate('/', { replace: true }), 1400);
      } catch (error) {
        setMessage(
          error instanceof ApiError
            ? error.offline
              ? 'Geen verbinding. Probeer het opnieuw zodra je wifi hebt.'
              : error.message
            : 'Er ging iets mis.'
        );
        setPhase('failed');
      }
    })();
  }, [navigate, refresh]);

  return (
    <div className="flex min-h-dvh flex-col items-center justify-center gap-5 px-6 text-center text-white">
      {phase === 'redeeming' && (
        <>
          <div className="text-5xl animate-pulse" aria-hidden>
            🂡
          </div>
          <p className="text-slate-400">Bezig met aanmelden…</p>
        </>
      )}

      {phase === 'done' && (
        <>
          <div className="text-6xl" aria-hidden>
            🎉
          </div>
          <div className="space-y-1">
            <h1 className="text-2xl font-bold">Je doet mee!</h1>
            <p className="text-slate-400">Dit toestel hoeft nooit meer in te loggen.</p>
          </div>
        </>
      )}

      {(phase === 'failed' || phase === 'nocode') && (
        <>
          <div className="text-5xl" aria-hidden>
            🤔
          </div>
          <div className="max-w-xs space-y-1">
            <h1 className="text-xl font-bold">Dat lukte niet</h1>
            <p className="text-slate-400">
              {phase === 'nocode'
                ? 'Er staat geen code in deze link. Scan de QR-code nog eens.'
                : message}
            </p>
            <p className="pt-2 text-sm text-slate-500">
              Een uitnodiging geldt vijftien minuten en werkt één keer. Vraag om een nieuwe.
            </p>
          </div>
        </>
      )}
    </div>
  );
}
